import React, { useEffect, useState } from "react";
import { Bell, BellOff, Info, Share } from "lucide-react";
import { toast } from "sonner";
import { getPushSupport, isPushEnabled, enablePush, disablePush } from "../lib/push";
import { useI18n } from "../context/I18nContext";

/**
 * Platform-aware notifications control (shared by home/account sheets and order tracking).
 * variant "row"  -> slate toggle row (account sheets)
 * variant "pill" -> white pill button (order tracking); hidden once enabled
 */
export function NotifyButton({ variant = "row" }) {
  const { t } = useI18n();
  const [support, setSupport] = useState(null);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const s = getPushSupport();
    setSupport(s);
    if (s.supported) isPushEnabled().then(setOn);
  }, []);
  if (!support) return null;

  const wrap = variant === "pill" ? "w-full bg-white rounded-2xl p-4 shadow-sm" : "mt-2 w-full bg-slate-100 rounded-2xl px-4 py-3.5";

  const enable = async () => {
    setBusy(true);
    const ok = await enablePush();
    setBusy(false);
    if (ok) { setOn(true); toast.success(t("notify.enabled_toast", "Obaveštenja uključena")); }
    else {
      const perm = "Notification" in window ? Notification.permission : "default";
      setSupport(getPushSupport());
      toast.message(perm === "denied" ? t("notify.blocked_toast", "Obaveštenja su blokirana u podešavanjima pregledača") : t("notify.enable_failed", "Nije moguće uključiti obaveštenja"));
    }
  };
  const disable = async () => { setBusy(true); await disablePush(); setOn(false); setBusy(false); toast.success(t("notify.disabled_toast", "Obaveštenja isključena")); };

  // iOS Safari (not installed): guide the user to install, do NOT show a generic error.
  if (support.reason === "ios-needs-install") {
    return (
      <div data-testid="notify-ios-install" className={`${wrap} text-left`}>
        <div className="flex items-start gap-2">
          <Info size={18} className="text-brand-red mt-0.5 shrink-0" />
          <div className="text-sm text-slate-600">
            <p className="font-semibold text-slate-800">{t("notify.ios_install", "Uključi obaveštenja instaliranjem Ponesi na početni ekran.")}</p>
            <button data-testid="notify-ios-help" onClick={() => setShowHelp((v) => !v)} className="mt-1 text-brand-red font-semibold">{t("notify.ios_how", "Kako da instaliram?")}</button>
            {showHelp && (
              <ol className="mt-2 list-decimal list-inside space-y-1 text-slate-500">
                <li>{t("notify.ios_step_share", "Dodirni Podeli")} <Share size={12} className="inline align-[-1px]" /></li>
                <li>{t("notify.ios_step_add", "Izaberi „Add to Home Screen“")}</li>
                <li>{t("notify.ios_step_open", "Otvori Ponesi sa početnog ekrana")}</li>
                <li>{t("notify.ios_step_enable", "Dodirni „Uključi obaveštenja“")}</li>
              </ol>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!support.supported) {
    return (
      <div data-testid="notify-unsupported" className={wrap}>
        <p className="text-sm text-slate-500 flex items-center gap-2"><BellOff size={16} /> {t("notify.unsupported", "Obaveštenja nisu podržana na ovom uređaju.")}</p>
      </div>
    );
  }

  if (support.permission === "denied" && !on) {
    return (
      <div data-testid="notify-denied" className={wrap}>
        <p className="text-sm text-slate-500 flex items-center gap-2"><BellOff size={16} /> {t("notify.blocked", "Obaveštenja su blokirana. Uključite ih u podešavanjima pregledača.")}</p>
      </div>
    );
  }

  if (variant === "row") {
    return (
      <button data-testid="notif-toggle" disabled={busy} onClick={on ? disable : enable}
        className="mt-2 w-full flex items-center justify-between bg-slate-100 rounded-2xl px-4 py-3.5 font-semibold disabled:opacity-60">
        <span className="flex items-center gap-2"><Bell size={18} /> {t("notify.title", "Obaveštenja o porudžbini")}</span>
        <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${on ? "bg-brand-green text-white" : "bg-slate-300 text-slate-700"}`}>{on ? t("notify.on", "Uključeno") : t("notify.off", "Isključeno")}</span>
      </button>
    );
  }

  if (on) return null;
  return (
    <button data-testid="enable-notif" disabled={busy} onClick={enable}
      className="w-full bg-white rounded-2xl p-4 flex items-center justify-center gap-2 font-semibold text-slate-700 shadow-sm disabled:opacity-60">
      <Bell size={16} /> {t("notify.enable_pill", "Uključi obaveštenja")}
    </button>
  );
}
